/**
 * Beacon de analítica propia (POST /api/analytics/visita + /salida).
 *
 * No hay librería ni script de terceros: son dos requests chicos contra el
 * mismo dominio. Nada de esto bloquea el render ni rompe la página si falla —
 * cada camino termina en un catch vacío a propósito.
 *
 * Qué se manda: el path, el referrer, el idioma del navegador y el ancho de
 * viewport. El resto (dispositivo, navegador, fuente) lo deriva el servidor del
 * user-agent, y la identidad es una cookie first-party. No se manda la IP ni
 * ningún dato del usuario.
 */

let iniciado = false;
let visitaId: string | null = null;
let comienzo = 0;
let scrollMaximo = 0;
let salidaEnviada = false;

function pathActual(): string {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

/** Porcentaje del documento visto, tolerante a páginas más cortas que la ventana. */
function scrollActual(): number {
  const doc = document.documentElement;
  const alto = Math.max(doc.scrollHeight, document.body?.scrollHeight ?? 0);
  if (alto <= window.innerHeight) return 100;
  const visto = ((window.scrollY + window.innerHeight) / alto) * 100;
  return Math.max(0, Math.min(100, Math.round(visto)));
}

function alScrollear() {
  const actual = scrollActual();
  if (actual > scrollMaximo) scrollMaximo = actual;
}

/**
 * Cierre de la visita. Se dispara en pagehide y también cuando la pestaña pasa
 * a oculta (en móvil el pagehide no siempre llega). El servidor solo acepta el
 * primer cierre de cada visita, así que mandarlo dos veces es inofensivo.
 */
function enviarSalida() {
  if (salidaEnviada || !visitaId) return;
  salidaEnviada = true;
  const cuerpo = JSON.stringify({
    id: Number(visitaId),
    duracionS: Math.min(86400, Math.round((Date.now() - comienzo) / 1000)),
    scrollPct: Math.max(scrollMaximo, scrollActual()),
  });
  try {
    // sendBeacon sobrevive al unload; el fetch keepalive es el plan B.
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/analytics/salida", new Blob([cuerpo], { type: "text/plain" }));
    } else {
      void fetch("/api/analytics/salida", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: cuerpo,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* sin analítica se sigue navegando igual */
  }
}

/** Registra la visita actual. Idempotente: en StrictMode se monta dos veces. */
export function iniciarSeguimiento(): void {
  if (iniciado) return;
  iniciado = true;

  const path = pathActual();
  // El panel no se mide a sí mismo.
  if (path === "/analytics" || path.startsWith("/analytics/")) return;

  comienzo = Date.now();
  const params = new URLSearchParams(window.location.search);

  void fetch("/api/analytics/visita", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      path,
      referrer: document.referrer || null,
      idioma: navigator.language || null,
      ancho: window.innerWidth || null,
      utmSource: params.get("utm_source"),
      utmCampaign: params.get("utm_campaign"),
    }),
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((datos: { id?: string } | null) => {
      if (datos?.id) visitaId = datos.id;
    })
    .catch(() => {});

  window.addEventListener("scroll", alScrollear, { passive: true });
  window.addEventListener("pagehide", enviarSalida);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") enviarSalida();
  });
}
